(() => {
  if (customElements.get('ice-moon-configurator')) return;

  class IceMoonConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-ice-configuration]').textContent);
        this.variants = JSON.parse(this.querySelector('[data-ice-variants]').textContent);
      } catch (error) {
        this.setStatus('Style options could not be loaded.');
        return;
      }
      this.availableStyles = (this.config.styles || []).filter(style =>
        this.variants.some(variant => variant.style === style.label)
      );
      this.styleButtons = Array.from(this.querySelectorAll('[data-style]'));
      const initialVariant = this.variants.find(variant =>
        String(variant.id) === this.dataset.selectedVariant
      );
      this.currentStyle = this.availableStyles.find(style =>
        initialVariant?.style === style.label
      ) || this.availableStyles[0];
      if (!this.currentStyle) {
        this.setStatus('No valid garment styles are available for this product.');
        return;
      }
      this.currentSize = initialVariant?.style === this.currentStyle.label ? initialVariant.size : null;
      this.currentColor = this.currentStyle.colors?.[0]?.name || null;

      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.dataset.style) {
          const next = this.availableStyles.find(style => style.key === button.dataset.style);
          if (!next) return;
          this.currentStyle = next;
          if (!next.colors.some(color => color.name === this.currentColor)) {
            this.currentColor = next.colors[0]?.name || null;
          }
          this.render();
        } else if (button.dataset.color) {
          if (!this.currentStyle.colors.some(color => color.name === button.dataset.color)) return;
          this.currentColor = button.dataset.color;
          this.render();
        } else if (button.dataset.size) {
          if (!this.getVariantsForStyle().some(variant =>
            variant.size === button.dataset.size && variant.available
          )) return;
          this.currentSize = button.dataset.size;
          this.render();
        }
      });

      this.render();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.syncForm(), { once: true });
      } else {
        this.syncForm();
      }
    }

    getVariantsForStyle() {
      if (!this.currentStyle) return [];
      return this.variants.filter(variant => variant.style === this.currentStyle.label);
    }

    setStatus(message) {
      const element = this.querySelector('[data-ice-status]');
      if (element) element.textContent = message;
    }

    render() {
      const guide = this.querySelector('[data-ice-chart]');
      if (guide) {
        const url = guide.dataset.override || this.currentStyle.sizeChart || '';
        if (/^https:\/\//i.test(url) || url.startsWith('/')) {
          guide.href = url;
          guide.hidden = false;
        } else {
          guide.removeAttribute('href');
          guide.hidden = true;
        }
      }
      const sizes = this.getVariantsForStyle();
      const available = sizes.filter(variant => variant.available);
      if (!available.some(variant => variant.size === this.currentSize)) {
        this.currentSize = available[0]?.size || null;
      }
      const colors = Array.isArray(this.currentStyle.colors) ? this.currentStyle.colors : [];
      if (!colors.some(color => color.name === this.currentColor)) {
        this.currentColor = colors[0]?.name || null;
      }

      this.styleButtons.forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.style === this.currentStyle.key));
      });
      this.querySelector('[data-selected-style]').textContent = this.currentStyle.label;
      this.querySelector('[data-selected-color]').textContent = this.currentColor || 'Unavailable';
      this.querySelector('[data-selected-size]').textContent = this.currentSize || 'Unavailable';

      const swatches = this.querySelector('[data-ice-colors]');
      swatches.replaceChildren();
      colors.forEach(color => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ice-moon-configurator__swatch';
        button.dataset.color = color.name;
        button.title = color.name;
        button.setAttribute('aria-label', 'Select color: ' + color.name);
        button.setAttribute('aria-pressed', String(color.name === this.currentColor));
        const inner = document.createElement('span');
        inner.style.backgroundColor = /^#[0-9a-fA-F]{6}$/.test(color.hex) ? color.hex : '#cccccc';
        button.appendChild(inner);
        swatches.appendChild(button);
      });

      const sizesElement = this.querySelector('[data-ice-sizes]');
      sizesElement.replaceChildren();
      sizes.forEach(variant => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ice-moon-configurator__size';
        button.dataset.size = variant.size;
        button.textContent = variant.size;
        button.disabled = !variant.available;
        button.setAttribute('aria-pressed', String(variant.size === this.currentSize));
        sizesElement.appendChild(button);
      });

      this.currentVariant = available.find(variant => variant.size === this.currentSize) || null;
      const price = this.querySelector('[data-ice-price]');
      if (this.currentVariant && this.currentColor) {
        const cents = Number(this.currentVariant.price);
        price.textContent = Number.isFinite(cents)
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: this.dataset.currency || 'USD'
            }).format(cents / 100)
          : 'Price unavailable';
        this.setStatus('');
      } else {
        price.textContent = 'Unavailable';
        this.setStatus('Please choose an available garment style, color, and size.');
      }
      this.syncForm();
      this.dispatchEvent(new CustomEvent('ice-moon:selection-change', {
        bubbles: true,
        detail: {
          style: this.currentStyle.key,
          color: this.currentColor,
          size: this.currentSize,
          variantId: this.currentVariant?.id || null
        }
      }));
    }

    syncForm() {
      this.querySelector('[data-ice-color-property]').value = this.currentColor || '';
      this.querySelector('[data-ice-style-property]').value = this.currentStyle?.label || '';
      this.querySelector('[data-ice-brand-property]').value = this.currentStyle?.brand || '';
      const form = document.getElementById(this.dataset.formId);
      if (!form) return;
      const variantId = form.querySelector('input[name="id"]');
      if (variantId) {
        variantId.value = this.currentVariant ? String(this.currentVariant.id) : '';
        variantId.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this._boundForm !== form) {
        if (this._boundForm && this._submitHandler) {
          this._boundForm.removeEventListener('submit', this._submitHandler, true);
        }
        this._submitHandler = event => {
          const idInput = form.querySelector('input[name="id"]');
          if (!this.currentVariant || !this.currentColor ||
              idInput?.value !== String(this.currentVariant.id)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.setStatus('Choose a valid style, color, and size before adding to cart.');
          }
        };
        form.addEventListener('submit', this._submitHandler, true);
        this._boundForm = form;
      }
    }

    disconnectedCallback() {
      if (this._boundForm && this._submitHandler) {
        this._boundForm.removeEventListener('submit', this._submitHandler, true);
      }
      this._boundForm = null;
      this._initialized = false;
    }
  }

  customElements.define('ice-moon-configurator', IceMoonConfigurator);
})();